package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.DropdownOption
import top.wcpe.allincore.ui.runtime.element.ProgressBar
import top.wcpe.allincore.ui.runtime.element.RadioButton
import top.wcpe.allincore.ui.runtime.element.RadioGroup
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.TextField
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.text.charCountOf
import top.wcpe.allincore.ui.runtime.internal.text.codePointAtCompat
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.render.model.GradientStop
import top.wcpe.allincore.ui.runtime.style.CornerRadii
import kotlin.jvm.JvmStatic

/**
 * 交互控件值属性贡献者：value / max / min / step / checked / placeholder / enabled /
 * selected / groupValue。
 *
 * 这些 key 在多个元素间复用且语义略有差异（如 value 在 Slider/ProgressBar 是 double、
 * 在 TextField 是 String），因此 lambda 内部仍有 instanceof 分支——这是当前能接受的折中，
 * 后续若控件激增，可考虑下沉到元素自身的 PropBinding 声明。
 */
@Suppress("TooManyFunctions")
internal object ControlValuePropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.ControlValuePropContributor")

    @JvmStatic
    @Suppress("CyclomaticComplexMethod", "LongMethod", "MagicNumber")
    fun contribute(m: MutableMap<String, PropBinding>) {
        m.put(
            "checked",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                when (el) {
                    is Checkbox -> el.checked(ValueCoercion.toBool(v))
                    // wiki DSL 用 checked 表达开关态；运行时 Switch 字段名为 enabled。
                    is Switch -> el.enabled(ValueCoercion.toBool(v))
                    else -> PropDiag.unsupported(LOGGER, el, "checked")
                }
            },
        )

        m.put(
            "value",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    when (el) {
                        is ProgressBar -> el.value(ValueCoercion.toDouble(v))
                        is Slider -> el.value(ValueCoercion.toDouble(v))
                        is TextField -> el.value(v.toString())
                        is DropdownOption -> el.value(v.toString())
                        // wiki radiogroup/dropdown 常用 value 作为 selectedValue 别名。
                        is RadioGroup -> el.selectedValue(v.toString())
                        is Dropdown -> el.selectedValue(v.toString())
                        // radio 子项 value 映射到 groupValue。
                        is RadioButton -> el.groupValue(v.toString())
                        else -> PropDiag.unsupported(LOGGER, el, "value")
                    }
                },
                { el ->
                    when (el) {
                        is Slider -> el.value()
                        is ProgressBar -> el.value()
                        is TextField -> el.value()
                        is RadioGroup -> el.selectedValue()
                        is Dropdown -> el.selectedValue()
                        is RadioButton -> el.groupValue()
                        else -> null
                    }
                },
            ),
        )

        m.put(
            "label",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    when (el) {
                        is DropdownOption -> el.label(v.toString())
                        // Radio 渲染器暂不画标签；吞掉 wiki 字面量以免刷 WARN。
                        is RadioButton -> Unit
                        else -> PropDiag.unsupported(LOGGER, el, "label")
                    }
                },
                { el -> if (el is DropdownOption) el.label() else null },
            ),
        )

        m.put(
            "max",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    when (el) {
                        is ProgressBar -> el.max(ValueCoercion.toDouble(v))
                        is Slider -> el.max(ValueCoercion.toDouble(v))
                        else -> PropDiag.unsupported(LOGGER, el, "max")
                    }
                },
                { el ->
                    when (el) {
                        is Slider -> el.max()
                        is ProgressBar -> el.max()
                        else -> null
                    }
                },
            ),
        )

        m.put(
            "min",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    if (el is Slider) {
                        el.min(ValueCoercion.toDouble(v))
                    } else {
                        PropDiag.unsupported(LOGGER, el, "min")
                    }
                },
                { el -> if (el is Slider) el.min() else null },
            ),
        )

        m.put(
            "step",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    if (el is Slider) {
                        el.step(ValueCoercion.toDouble(v))
                    } else {
                        PropDiag.unsupported(LOGGER, el, "step")
                    }
                },
                { el -> if (el is Slider) el.step() else null },
            ),
        )

        m.put(
            "placeholder",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                when (el) {
                    is TextField -> el.placeholder(v.toString())
                    is Slot -> el.placeholder(v.toString())
                    else -> PropDiag.unsupported(LOGGER, el, "placeholder")
                }
            },
        )

        m.put(
            "enabled",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                when (el) {
                    is Switch -> el.enabled(ValueCoercion.toBool(v))
                    is Button -> {
                        // M6-f §8.1：button 的 enabled 仅控制点击事件触发，渲染暂不变化
                        el.enabled(ValueCoercion.toBool(v))
                    }
                    is TextField -> el.enabled(ValueCoercion.toBool(v))
                    else -> PropDiag.unsupported(LOGGER, el, "enabled")
                }
            },
        )

        m.put(
            "selected",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is RadioButton) {
                    el.selected(ValueCoercion.toBool(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "selected")
                }
            },
        )

        m.put(
            "groupValue",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is RadioButton) {
                    el.groupValue(v.toString())
                } else {
                    PropDiag.unsupported(LOGGER, el, "groupValue")
                }
            },
        )

        // Stage 14：HUD 场景下需要按物理意义区分多条进度条颜色（血/饥饿/经验），
        // 仅 ProgressBar 支持；其它控件（Slider 等）保持主题色不变。
        m.put(
            "barColor",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    if (el is ProgressBar) {
                        el.barColor(ValueCoercion.toIntColor(v))
                    } else {
                        PropDiag.unsupported(LOGGER, el, "barColor")
                    }
                },
                { el -> if (el is ProgressBar) el.barColor() else null },
            ),
        )

        // Boss 血条：复刻 vanilla NOTCHED_6/10/12/20 等分刻度。
        // 仅 ProgressBar 支持；0 或负值表示不绘制刻度。
        m.put(
            "notches",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    if (el is ProgressBar) {
                        el.notches(ValueCoercion.toFloat(v).toInt())
                    } else {
                        PropDiag.unsupported(LOGGER, el, "notches")
                    }
                },
                { el -> if (el is ProgressBar) el.notches().toDouble() else null },
            ),
        )

        // 进度过渡：value 变化时前景平滑收敛而非瞬间跳变。仅 ProgressBar 支持。
        m.put(
            "animateValue",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    if (el is ProgressBar) {
                        el.animateValue(ValueCoercion.toBool(v))
                    } else {
                        PropDiag.unsupported(LOGGER, el, "animateValue")
                    }
                },
                { el -> if (el is ProgressBar) el.animateValue() else null },
            ),
        )

        m.put(
            "valueTransitionMs",
            PropBinding.of(
                { el, v ->
                    if (v == null) return@of
                    if (el is ProgressBar) {
                        el.valueTransitionMs(ValueCoercion.toDouble(v))
                    } else {
                        PropDiag.unsupported(LOGGER, el, "valueTransitionMs")
                    }
                },
                { el -> if (el is ProgressBar) el.valueTransitionMs() else null },
            ),
        )

        // ========== ProgressBar v2：前景独立圆角 ==========

        m.put(
            "barCornerRadius",
            PropBinding.writeOnly { el, v ->
                if (el !is ProgressBar) {
                    if (v != null) PropDiag.unsupported(LOGGER, el, "barCornerRadius")
                    return@writeOnly
                }
                val pb = el
                if (v == null) {
                    pb.barCornerRadii(null)
                    return@writeOnly
                }
                if (v is List<*>) {
                    val nums = ArrayList<Number>(v.size)
                    for (item in v) {
                        if (item is Number) {
                            nums.add(item)
                        } else {
                            LOGGER.warn("barCornerRadius 数组元素期望数字")
                            return@writeOnly
                        }
                    }
                    pb.barCornerRadii(CornerRadii.parseCss(nums))
                    return@writeOnly
                }
                pb.barCornerRadius(ValueCoercion.toFloat(v))
            },
        )

        m.put("barCornerRadiusTL", PropBinding.writeOnly { el, v -> putBarCorner(el, v, 0) })
        m.put("barCornerRadiusTR", PropBinding.writeOnly { el, v -> putBarCorner(el, v, 1) })
        m.put("barCornerRadiusBR", PropBinding.writeOnly { el, v -> putBarCorner(el, v, 2) })
        m.put("barCornerRadiusBL", PropBinding.writeOnly { el, v -> putBarCorner(el, v, 3) })

        // ========== ProgressBar v2：前景渐变 ==========

        m.put(
            "barGradientType",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barGradientType") { pb ->
                    pb.barGradientType(v?.toString())
                }
            },
        )
        m.put(
            "barGradientAngle",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barGradientAngle") { pb ->
                    pb.barGradientAngle(v?.let { ValueCoercion.toFloat(it) })
                }
            },
        )
        m.put(
            "barGradientStartColor",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barGradientStartColor") { pb ->
                    pb.barGradientStartColor(v?.let { ValueCoercion.toIntColor(it) })
                }
            },
        )
        m.put(
            "barGradientEndColor",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barGradientEndColor") { pb ->
                    pb.barGradientEndColor(v?.let { ValueCoercion.toIntColor(it) })
                }
            },
        )
        m.put(
            "barRadialCenterX",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barRadialCenterX") { pb ->
                    pb.barRadialCenterX(v?.let { ValueCoercion.toFloat(it) })
                }
            },
        )
        m.put(
            "barRadialCenterY",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barRadialCenterY") { pb ->
                    pb.barRadialCenterY(v?.let { ValueCoercion.toFloat(it) })
                }
            },
        )
        m.put(
            "barRadialRadius",
            PropBinding.writeOnly { el, v ->
                whenProgressBar(el, "barRadialRadius") { pb ->
                    pb.barRadialRadius(v?.let { ValueCoercion.toFloat(it) })
                }
            },
        )

        // barGradientStops: List<List<Number/Color>>，与 visual.gradientStops 同结构
        m.put(
            "barGradientStops",
            PropBinding.writeOnly { el, v ->
                if (el !is ProgressBar) {
                    if (v != null) PropDiag.unsupported(LOGGER, el, "barGradientStops")
                    return@writeOnly
                }
                val pb = el
                if (v == null) {
                    pb.barGradientStops(null)
                    return@writeOnly
                }
                if (v !is List<*>) {
                    LOGGER.warn("barGradientStops 期望 List，实际 ${v::class.simpleName}")
                    return@writeOnly
                }
                val stops = ArrayList<GradientStop>(v.size)
                for (i in v.indices) {
                    val item = v[i]
                    if (item !is List<*> || item.size < 2) {
                        LOGGER.warn("barGradientStops[$i] 期望 [offset, argbColor]，已跳过")
                        continue
                    }
                    val pair = item
                    val offset = ValueCoercion.toFloat(pair[0])
                    val argb = ValueCoercion.toIntColor(pair[1])
                    stops.add(GradientStop(offset, argb))
                }
                pb.barGradientStops(stops)
            },
        )

        // ========== TextField 光标样式 ==========

        m.put(
            "caretWidth",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.caretWidth(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "caretWidth")
                }
            },
        )

        m.put(
            "caretColor",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.caretColor(ValueCoercion.toIntColor(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "caretColor")
                }
            },
        )

        m.put(
            "caretBlink",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.caretBlink(ValueCoercion.toBool(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "caretBlink")
                }
            },
        )

        m.put(
            "caretBlinkRate",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.caretBlinkRate(ValueCoercion.toFloat(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "caretBlinkRate")
                }
            },
        )

        // 密码模式：渲染时用掩码字符遮蔽内容，真实 value 不变。
        m.put(
            "password",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.password(ValueCoercion.toBool(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "password")
                }
            },
        )

        // 只读模式：可聚焦但不可编辑。
        m.put(
            "readOnly",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.readOnly(ValueCoercion.toBool(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "readOnly")
                }
            },
        )

        // 最大输入字符数：0 表示不限制。
        m.put(
            "maxLength",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.maxLength(ValueCoercion.toFloat(v).toInt())
                } else {
                    PropDiag.unsupported(LOGGER, el, "maxLength")
                }
            },
        )

        // 密码掩码字符：取首个完整 code point，默认 '*'。
        // 掩码字段为 char，仅能承载 BMP 字符；若 DSL 传入非 BMP 字符（如 emoji），
        // 取 charAt(0) 会拿到半个代理对画出破碎字符，此处直接忽略以保留默认掩码。
        m.put(
            "passwordChar",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    val s = v.toString()
                    if (s.isNotEmpty()) {
                        val cp = s.codePointAtCompat(0)
                        if (charCountOf(cp) == 1) el.passwordChar(cp.toChar())
                    }
                } else {
                    PropDiag.unsupported(LOGGER, el, "passwordChar")
                }
            },
        )

        // 文本颜色：ARGB hex，0 跟随主题。
        m.put(
            "textColor",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.textColor(ValueCoercion.toIntColor(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "textColor")
                }
            },
        )

        // 注：multiline 由 ButtonStatePropContributor 统一登记（晚于本贡献者注册会覆盖同名 key），
        // TextField 分支已并入该处（同 textAlign），此处不再重复 put。

        // 选区高亮色：ARGB hex；不设置时跟随主题 primary 叠半透明 alpha。
        m.put(
            "selectionColor",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.selectionColor(ValueCoercion.toIntColor(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "selectionColor")
                }
            },
        )

        // Enter 提交策略覆盖：true=裸 Enter 始终提交；false=Enter 永不提交（多行下仅换行）。
        m.put(
            "submitOnEnter",
            PropBinding.writeOnly { el, v ->
                if (v == null) return@writeOnly
                if (el is TextField) {
                    el.submitOnEnter(ValueCoercion.toBool(v))
                } else {
                    PropDiag.unsupported(LOGGER, el, "submitOnEnter")
                }
            },
        )
        // 注：textAlign 由 ButtonStatePropContributor 统一登记（晚于本贡献者注册会覆盖同名 key），
        // TextField 分支已并入该处，此处不再重复 put。
    }

    /** 单角圆角写入 helper：`cornerIdx` 0=TL / 1=TR / 2=BR / 3=BL。 */
    @Suppress("ComplexCondition", "MagicNumber")
    private fun putBarCorner(el: UIElement<*>, v: Any?, cornerIdx: Int) {
        if (el !is ProgressBar) {
            if (v != null) PropDiag.unsupported(LOGGER, el, "barCornerRadiusXX")
            return
        }
        if (v == null) return
        val r = ValueCoercion.toFloat(v)
        val cur = el.barCornerRadii() ?: CornerRadii.ZERO
        val arr = cur.toArray()
        arr[cornerIdx] = r
        el.barCornerRadii(CornerRadii.of(arr[0], arr[1], arr[2], arr[3]))
    }

    /** 限定 ProgressBar：分发到 setter；其它元素 warn 并跳过。 */
    private fun whenProgressBar(el: UIElement<*>, name: String, setter: (ProgressBar) -> Unit) {
        if (el !is ProgressBar) {
            PropDiag.unsupported(LOGGER, el, name)
            return
        }
        setter(el)
    }
}
